import { apiClient } from './client';

export type AnnouncementType = 'info' | 'warning' | 'success' | 'critical';
export type AnnouncementDisplay = 'banner' | 'modal' | 'both';
export type AnnouncementTrialStatus = 'any' | 'trial' | 'paid';

/** The trimmed shape shown to a viewer (no targeting internals). */
export interface ActiveAnnouncement {
  _id: string;
  title: string;
  body: string;
  type: AnnouncementType;
  display: AnnouncementDisplay;
  dismissible: boolean;
  createdAt: string;
}

export interface AnnouncementAudience {
  roles?: string[];
  cantons?: string[];
  trialStatus?: AnnouncementTrialStatus;
}

/** Full admin view. */
export interface Announcement extends ActiveAnnouncement {
  audience: AnnouncementAudience;
  startAt: string | null;
  endAt: string | null;
  isActive: boolean;
  createdBy?: string;
  stats?: { seen: number; dismissed: number };
}

export interface AnnouncementPage {
  items: Announcement[];
  total: number;
  page: number;
  totalPages: number;
}

export interface CreateAnnouncementInput {
  title: string;
  body: string;
  type?: AnnouncementType;
  display?: AnnouncementDisplay;
  dismissible?: boolean;
  audience?: AnnouncementAudience;
  startAt?: string | null;
  endAt?: string | null;
  isActive?: boolean;
}

export const announcementsApi = {
  // ─── User-facing ────────────────────────────────────────────────────────
  active: async (): Promise<ActiveAnnouncement[]> => {
    const res = await apiClient.get<{ success: boolean; data: ActiveAnnouncement[] }>(
      '/announcements/active'
    );
    return res.data;
  },
  seen: async (id: string): Promise<void> => {
    await apiClient.post(`/announcements/${id}/seen`, {});
  },
  dismiss: async (id: string): Promise<void> => {
    await apiClient.post(`/announcements/${id}/dismiss`, {});
  },

  // ─── Admin ──────────────────────────────────────────────────────────────
  create: async (input: CreateAnnouncementInput): Promise<Announcement> => {
    const res = await apiClient.post<{ success: boolean; data: Announcement }>(
      '/announcements',
      input
    );
    return res.data;
  },
  list: async (page = 1): Promise<AnnouncementPage> => {
    const res = await apiClient.get<{ success: boolean; data: AnnouncementPage }>(
      `/announcements?page=${page}`
    );
    return res.data;
  },
  get: async (id: string): Promise<Announcement> => {
    const res = await apiClient.get<{ success: boolean; data: Announcement }>(`/announcements/${id}`);
    return res.data;
  },
  update: async (id: string, input: Partial<CreateAnnouncementInput>): Promise<Announcement> => {
    const res = await apiClient.patch<{ success: boolean; data: Announcement }>(
      `/announcements/${id}`,
      input
    );
    return res.data;
  },
  remove: async (id: string): Promise<Announcement> => {
    const res = await apiClient.delete<{ success: boolean; data: Announcement }>(
      `/announcements/${id}`
    );
    return res.data;
  },
};
